import { Link, useLocation } from "react-router-dom";
import { Shirt, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  isNavigationItemActive,
  navigationSections,
} from "@/components/layout/navigation";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/utils/cn";

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function Sidebar({ open, setOpen }: SidebarProps) {
  const location = useLocation();
  const currentRole = useAuthStore((state) => state.user?.role ?? "user");
  const visibleSections = navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        !item.roles || item.roles.includes(currentRole),
      ),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      {open ? (
        <div
          className="fixed inset-0 z-30 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          "fixed left-0 top-0 z-40 flex h-full flex-col border-r border-border/80 bg-card/95 backdrop-blur-xl transition-all duration-300 ease-in-out",
          open ? "w-72 translate-x-0" : "w-20 -translate-x-full lg:translate-x-0",
          "lg:translate-x-0",
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border/80 px-4">
          <Link
            to="/dashboard"
            className={cn("flex min-w-0 items-center gap-3", !open && "mx-auto")}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/12 text-primary">
              <Shirt className="h-5 w-5" />
            </div>
            {open ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">ClothesWeb Admin</p>
                <p className="truncate text-xs text-muted-foreground">
                  Catalog operations
                </p>
              </div>
            ) : null}
          </Link>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {visibleSections.map((section) => (
            <div key={section.title} className="mb-6 last:mb-0">
              {open ? (
                <p className="mb-2 px-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {section.title}
                </p>
              ) : null}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isNavigationItemActive(item, location.pathname);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      title={open ? undefined : item.label}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          setOpen(false);
                        }
                      }}
                      className={cn(
                        "group flex items-center gap-3 rounded-2xl border px-3 py-3 text-sm transition-colors",
                        active
                          ? "border-primary/30 bg-primary/10 text-foreground"
                          : "border-transparent text-muted-foreground hover:border-border/80 hover:bg-muted/50 hover:text-foreground",
                        !open && "justify-center px-0",
                      )}
                    >
                      <Icon className="h-5 w-5 flex-none" />
                      {open ? (
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-medium">{item.label}</span>
                            {item.comingSoon ? (
                              <span className="rounded-full border border-border/80 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                Soon
                              </span>
                            ) : null}
                          </div>
                          <p className="truncate text-xs text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

      </aside>
    </>
  );
}
